import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as S from './application.style';
import { ChevronLeft, Upload, FileText, ArrowRight } from 'lucide-react';
import { LoadingScreen } from '../../../components/loadingScreen';
import { getApplicationDetails } from '../../../services/application';
import { clearItem, setItem } from '../../../helper/localStorageHelper';
import { IntlMessages } from '../../../helper/langHelper';
import { clearCache, clearSummaryCache } from '../../../helper/cacheHelper';

const ApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const {
    data: applicationData,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['applicationDetails', id],
    queryFn: () => getApplicationDetails({ applicationId: id }),
    enabled: !!id,
    // staleTime: 5 * 60 * 1000,
    // cacheTime: 10 * 60 * 1000,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleDocUpload = (applicationId, precedureId) => {
    setItem('applicationId', applicationId);
    setItem('procedure', precedureId);
    navigate('/documentUpload', { state: { applicationId } });
  };

  const handlePreLodgementReportView = (applicationId, precedureId) => {
    setItem('applicationId', applicationId);
    setItem('procedure', precedureId);
    navigate('/preLodgementReport');
  };

  const handleFinalReportView = (applicationId, precedureId) => {
    setItem('applicationId', applicationId);
    setItem('procedure', precedureId);
    navigate('/reportReview');
  };

  const handleConvertToFinalLodgement = () => {
    clearCache();
    clearSummaryCache();
    clearItem('applicationId');
    clearItem('screenshotId');
    const appInfo = applicationData?.applicationInfo;

    navigate('/generateQuestion', {
      state: {
        selectedProcedure: appInfo?.procedureId,
        searchAddress: appInfo,
        overlays: appInfo?.overlayArray,
        fullName: appInfo?.fullName,
        inputValue: appInfo?.description,
        email: appInfo?.email,
        phone: appInfo?.phoneNumber,
        zoneDetailsData: appInfo?.propertyDetails,
        screenshotId: appInfo?.screenshotId || null,
        applicationId: appInfo?._id,
      },
    });
  };

  const handleMouseEnter = (e) => {
    const isPrelodgementApproved = applicationData?.prelodgementReportStatus;
    if (isPrelodgementApproved) {
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      setTooltipPosition({
        x: rect.left + scrollLeft + rect.width / 2,
        y: rect.top + scrollTop - 15,
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  if (loading) {
    return <LoadingScreen loadingText={<IntlMessages id="loading.application.details" />} />;
  }

  if (error) {
    return (
      <S.Container>
        <S.BackButton onClick={handleBack}>← Back</S.BackButton>
        <S.ErrorContainer>
          <p>Error: {error.message}</p>
          <p>Please try again later or contact support if the problem persists.</p>
        </S.ErrorContainer>
      </S.Container>
    );
  }

  const isPrelodgementApproved =
    applicationData?.prelodgementReportStatus && !applicationData?.finalReportStatus;

  return (
    <S.Main role="main">
      <S.ApplicationContainer>
        <S.Container>
          <S.BackButton onClick={handleBack} aria-label="Go back to applications list">
            <ChevronLeft size={20} /> <IntlMessages id="button.label.back" />
          </S.BackButton>

          <S.TooltipContent
            show={showTooltip}
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            Please click &quot;Convert to Final Lodgement&quot; button to complete the step before
            uploading
          </S.TooltipContent>

          <S.FlexLayout>
            {/* Left Section - Application Details */}
            <S.ApplicationCardWrapper>
              <S.Header>
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '10px' }}>
                  <S.ProjectTypeTag>
                    {applicationData?.applicationInfo?.procedureId?.procedureName}
                  </S.ProjectTypeTag>
                  <S.StatusBadge status={'pending'}>pending</S.StatusBadge>
                </div>
                {applicationData?.applicationInfo?.createdAt && (
                  <S.ProcedureId>
                    <IntlMessages id="card.text.submitted.date" />:{' '}
                    {new Date(applicationData?.applicationInfo?.createdAt).toLocaleDateString(
                      'en-GB'
                    )}
                  </S.ProcedureId>
                )}
              </S.Header>

              <S.Section>
                <S.SectionTitle>
                  <IntlMessages id="property.details" />
                </S.SectionTitle>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="property.address" />
                  </S.DetailLabel>
                  <S.Address>{applicationData?.applicationInfo?.address}</S.Address>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="property.division" />
                  </S.DetailLabel>
                  <S.Address>
                    {applicationData?.applicationInfo?.propertyDetails?.division || 'N/A'}
                  </S.Address>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="property.district" />
                  </S.DetailLabel>
                  <S.Address>
                    {applicationData?.applicationInfo?.propertyDetails?.district || 'N/A'}
                  </S.Address>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="property.description" />
                  </S.DetailLabel>
                  <S.DetailValue>{applicationData?.applicationInfo?.description}</S.DetailValue>
                </S.DetailItem>
              </S.Section>

              <S.Section>
                <S.SectionTitle>
                  <IntlMessages id="applicant.information" />
                </S.SectionTitle>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="applicant.name" />
                  </S.DetailLabel>
                  <S.DetailValue>{applicationData?.applicationInfo?.fullName}</S.DetailValue>
                </S.DetailItem>

                <S.DetailItem>
                  <S.DetailLabel>
                    <IntlMessages id="applicant.contact" />
                  </S.DetailLabel>
                  <S.DetailValue>{applicationData?.applicationInfo?.email}</S.DetailValue>
                  <S.DetailValue>{applicationData?.applicationInfo?.phoneNumber}</S.DetailValue>
                </S.DetailItem>
              </S.Section>
            </S.ApplicationCardWrapper>

            {/* Right Section - Overlays */}
            <S.OverlaysCard>
              {applicationData?.prelodgementReportStatus !== undefined && (
                <>
                  <S.SectionTitle style={{ marginTop: '20px' }}>
                    <IntlMessages id="reports.title" />
                  </S.SectionTitle>

                  <S.ReportItem>
                    <S.OverlayName>
                      <IntlMessages id="reports.prelodgement" />
                    </S.OverlayName>
                    {applicationData.prelodgementReportStatus ? (
                      <S.ViewDocumentLink
                        onClick={() =>
                          handlePreLodgementReportView(
                            applicationData.applicationInfo._id,
                            applicationData?.applicationInfo?.procedureId
                          )
                        }
                      >
                        <FileText size={14} />
                        <IntlMessages id="reports.view" />
                      </S.ViewDocumentLink>
                    ) : (
                      <S.StatusTag>
                        <IntlMessages id="reports.notAvailable" />
                      </S.StatusTag>
                    )}
                  </S.ReportItem>

                  {/* Convert to Final Lodgement Button */}
                  {isPrelodgementApproved && (
                    <S.ConvertButton onClick={handleConvertToFinalLodgement}>
                      <IntlMessages
                        id="reports.convertToFinal"
                        defaultMessage="Convert to Final Lodgement"
                      />
                      <ArrowRight size={16} />
                    </S.ConvertButton>
                  )}

                  {applicationData?.finalReportStatus !== undefined && (
                    <S.ReportItem isLast={true}>
                      <S.OverlayName>
                        <IntlMessages id="reports.final" />
                      </S.OverlayName>
                      {applicationData.pdfUploadStatus ? (
                        <S.ViewDocumentLink
                          onClick={() =>
                            handleFinalReportView(
                              applicationData.applicationInfo._id,
                              applicationData?.applicationInfo?.procedureId
                            )
                          }
                        >
                          <FileText size={14} />
                          <IntlMessages id="reports.view" />
                        </S.ViewDocumentLink>
                      ) : (
                        <S.StatusTag>
                          <IntlMessages id="reports.notAvailable" />
                        </S.StatusTag>
                      )}
                    </S.ReportItem>
                  )}
                </>
              )}

              <S.SectionTitle>
                <IntlMessages id="overlays.title" />
              </S.SectionTitle>

              {applicationData?.applicationInfo?.overlayArray &&
              applicationData.applicationInfo.overlayArray.length > 0 ? (
                <S.TooltipWrapper>
                  {isPrelodgementApproved ? (
                    <S.DisabledOverlay>
                      <S.OverlayList>
                        {applicationData.applicationInfo.overlayArray.map((item, index) => (
                          <S.OverlayItem key={index}>
                            <S.OverlayName>{item.overlay}</S.OverlayName>
                            {item.documentId ? (
                              <S.ViewDocumentLink>
                                <IntlMessages id="overlays.uploaded" />
                              </S.ViewDocumentLink>
                            ) : (
                              <S.UploadButton
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                onClick={(e) => e.preventDefault()}
                              >
                                <Upload size={14} />
                                <IntlMessages id="overlays.upload" />
                              </S.UploadButton>
                            )}
                          </S.OverlayItem>
                        ))}
                      </S.OverlayList>
                    </S.DisabledOverlay>
                  ) : (
                    <S.OverlayList>
                      {applicationData.applicationInfo.overlayArray.map((item, index) => (
                        <S.OverlayItem key={index}>
                          <S.OverlayName>{item.overlay}</S.OverlayName>
                          {item.documentId ? (
                            <S.ViewDocumentLink>
                              <IntlMessages id="overlays.uploaded" />
                            </S.ViewDocumentLink>
                          ) : (
                            <S.UploadButton
                              onClick={() =>
                                handleDocUpload(
                                  applicationData.applicationInfo._id,
                                  applicationData?.applicationInfo?.procedureId
                                )
                              }
                            >
                              <Upload size={14} />
                              <IntlMessages id="overlays.upload" />
                            </S.UploadButton>
                          )}
                        </S.OverlayItem>
                      ))}
                    </S.OverlayList>
                  )}
                </S.TooltipWrapper>
              ) : (
                <S.EmptyState>
                  <IntlMessages id="overlays.none" />
                </S.EmptyState>
              )}

              {
                applicationData?.applicationInfo?.pdfArray &&
                  applicationData.applicationInfo.pdfArray.length > 0 && (
                    <>
                      <S.SectionTitle style={{ marginTop: '20px' }}>
                        <IntlMessages id="documents.title" />
                      </S.SectionTitle>
                      <S.TooltipWrapper>
                        {isPrelodgementApproved ? (
                          <S.DisabledOverlay>
                            <S.OverlayList>
                              {applicationData.applicationInfo.pdfArray.map((item, index) => (
                                <S.OverlayItem key={index}>
                                  <S.OverlayName>{item.pdfId.pdfTitle}</S.OverlayName>
                                  {item.documentId ? (
                                    <S.ViewDocumentLink>
                                      <IntlMessages id="documents.uploaded" />
                                    </S.ViewDocumentLink>
                                  ) : (
                                    <S.UploadButton
                                      onMouseEnter={handleMouseEnter}
                                      onMouseLeave={handleMouseLeave}
                                      onClick={(e) => e.preventDefault()}
                                    >
                                      <Upload size={14} />
                                      <IntlMessages id="documents.upload" />
                                    </S.UploadButton>
                                  )}
                                </S.OverlayItem>
                              ))}
                            </S.OverlayList>
                          </S.DisabledOverlay>
                        ) : (
                          <S.OverlayList>
                            {applicationData.applicationInfo.pdfArray.map((item, index) => (
                              <S.OverlayItem key={index}>
                                <S.OverlayName>{item.pdfId.pdfTitle}</S.OverlayName>
                                {item.documentId ? (
                                  <S.ViewDocumentLink>
                                    <IntlMessages id="documents.uploaded" />
                                  </S.ViewDocumentLink>
                                ) : (
                                  <S.UploadButton
                                    onClick={() =>
                                      handleDocUpload(
                                        applicationData.applicationInfo._id,
                                        applicationData?.applicationInfo?.procedureId
                                      )
                                    }
                                  >
                                    <Upload size={14} />
                                    <IntlMessages id="documents.upload" />
                                  </S.UploadButton>
                                )}
                              </S.OverlayItem>
                            ))}
                          </S.OverlayList>
                        )}
                      </S.TooltipWrapper>
                    </>
                  )
                // : (
                //   <S.EmptyState>
                //     <IntlMessages id="documents.none" />
                //   </S.EmptyState>
                // )
              }
            </S.OverlaysCard>
          </S.FlexLayout>
        </S.Container>
      </S.ApplicationContainer>
    </S.Main>
  );
};

export default ApplicationDetails;
